<script lang="ts" setup>
import {useRoute, useRouter} from "vue-router";
import useCustomerLoyaltyCardMembership
    from "@/customer/composables/loyalty-card-membership/useCustomerLoyaltyCardMembership";
import useCustomerLoyaltyCardMembershipTransactions
    from "@/customer/composables/loyalty-card-membership/useCustomerLoyaltyCardMembershipTransactions";
import {onMounted, ref} from "vue";
import {
    LoyaltyCardMembershipHeader,
    LoyaltyCardTeaser,
    LoyaltyCardMembershipInformation,
    LoyaltyCardMembershipTransactions,
    LoyaltyCardMembershipRewardBanner
} from "@/loyalty-card-membership/components";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";

const route = useRoute();
const router = useRouter();

const customerId = Number(route.params.customerId);
const membershipId = Number(route.params.membershipId);

const selectedType = ref<null | string>(null);

/**
 * Arrived through the counter popup's "Offrir" shortcut: the offer
 * dialog opens on its own. The flag is dropped from the URL right away so a
 * reload or a back navigation does not reopen it (the parent router-view is
 * keyed on the path, not the full path, so this does not remount the screen).
 */
const openReward = ref(route.query.reward === '1');

onMounted(() => {
    if (openReward.value) {
        const {reward: _reward, ...query} = route.query;

        router.replace({query});
    }
});

const {membership, isLoading, mutate: mutateMembership} = useCustomerLoyaltyCardMembership(customerId, membershipId);
const {
    transactions,
    isLoading: isLoadingTransactions,
    isLoadingMore,
    error: transactionsError,
    hasMore,
    loadMore,
    refresh: refreshTransactions
} = useCustomerLoyaltyCardMembershipTransactions(customerId, membershipId, selectedType);

// A credit or a reward lands at the top of the history: it starts over from its first page.
const refresh = () => {
    refreshTransactions();
    mutateMembership();
};
</script>

<template>
  <div v-if="isLoading">
    <BaseFlexSpinner />
  </div>
  <div
    v-else-if="membership"
    class="m-6"
  >
    <LoyaltyCardMembershipRewardBanner
      :membership="membership"
      :customer-id="customerId"
      :open-reward="openReward"
      class="mb-4"
      @refresh="refresh"
    />
    <div class="flex flex-row justify-between flex-wrap gap-4">
      <LoyaltyCardMembershipHeader
        :customer-id="customerId"
        :membership="membership"
        :open-reward="openReward"
        @refresh="refresh"
      />
    </div>
    <LoyaltyCardTeaser :loyalty-card="membership.loyalty_card" />

    <LoyaltyCardMembershipTransactions
      :transactions="transactions"
      :selected-type="selectedType"
      :loading="isLoadingTransactions"
      :loading-more="isLoadingMore"
      :failed="!!transactionsError"
      :has-more="hasMore"
      @type-selected="type => selectedType = type"
      @load-more="loadMore"
      @retry="refreshTransactions"
    />
    <LoyaltyCardMembershipInformation :membership="membership" />
  </div>
</template>
